import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useRegion } from '../hooks/useRegion';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogBody } from '../components/ui/dialog';
import { getStatusColor } from '../lib/utils';
import { getFiscalYear, getPhonePlaceholder } from '../lib/regionConfig';
import { computeJobFinancials, getCompletionDate } from '../lib/jobFinancials';
import { Badge } from '../components/ui/badge';
import { JobKanban } from '../components/JobKanban';
import { UniversalSearch } from '../components/dashboard';
import { SystemAlertsBanner } from '../components/SystemAlertsBanner';
import { toast } from 'sonner';
import {
  Plus,
  ArrowRight,
  Package,
  Users,
  Loader2,
  Check,
  UserPlus,
  TrendingUp,
  X
} from 'lucide-react';
import { Illustration } from '../components/Illustration';
import { PageToolbar } from '../components/PageToolbar';
import { PhonePanel } from '../components/PhonePanel';
import { useIsPhone } from '../hooks/useIsPhone';

// Corner illustration for the stat cards; desktop only, where the cards are
// wide enough that it never sits under a figure.
const StatArt = ({ name }) => (
  <div className="absolute right-3 bottom-1 w-20 hidden lg:block pointer-events-none">
    <Illustration name={name} className="w-full aspect-square" />
  </div>
);


const EQUIPMENT_CATEGORIES = ['Camera', 'Lens', 'Lighting', 'Audio', 'Grip', 'Monitor', 'Accessories', 'Other'];

// Dashboard Job Card Component - uses consistent theming based on job ID


export default function DashboardPage() {
  const navigate = useNavigate();
  const { dashboardStats, fetchDashboardStats, jobs, jobsLoading, fetchJobs, createEquipment, createClient, updateClient, equipment, fetchEquipment, clients, fetchClients } = useAppStore();
  const { formatCurrency, regionConfig, region, getTaxName } = useRegion();
  const isPhone = useIsPhone();

  // Equipment dialog state
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState({
    name: '',
    category: 'Camera',
    description: '',
    purchase_price: '',
    daily_rate: '',
    weekly_rate: '',
    purchase_date: '',
  });

  // Client dialog state
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    business_name: '',
    billing_address: '',
    contacts: [],
  });
  
  // First-time user check - determine based on jobs
  const showOnboarding = !jobsLoading && jobs.length === 0;

  useEffect(() => {
    fetchDashboardStats();
    fetchJobs();
    fetchEquipment();
    fetchClients();
  }, [fetchDashboardStats, fetchJobs, fetchEquipment, fetchClients]);
  
  // Track completion of onboarding steps
  const hasEquipment = equipment.length > 0;
  const hasClients = clients.length > 0;

  // Sort jobs: booked first, then by most recent
  const bookedJobs = jobs.filter(j => j.status === 'booked').length;
  const jobsToChase = jobs.filter(j => j.status === 'draft' || j.status === 'sent').length;

  // Potential billings: open opportunities (draft + sent jobs)
  const openJobs = jobs.filter(j => j.status === 'draft' || j.status === 'sent');
  const potentialTotal = openJobs.reduce((sum, job) => sum + (job.totals?.grand || 0), 0);
  const potentialLabour = openJobs.reduce((sum, job) => sum + (job.totals?.labour || job.totals?.labor || 0), 0);
  const potentialEquipment = openJobs.reduce((sum, job) => sum + (job.totals?.equipment || 0), 0);

  // This financial year: profit and tax collected on completed and paid jobs
  const nowFy = getFiscalYear(new Date().toISOString(), region);
  const fyLabel = nowFy ? nowFy.label : 'This FY';
  let fyProfit = 0;
  let fyTax = 0;
  jobs.forEach((job) => {
    if (!['completed', 'paid'].includes(job.status)) return;
    const dateStr = getCompletionDate(job);
    const jobFy = dateStr ? getFiscalYear(dateStr, region) : null;
    if (!jobFy || !nowFy || jobFy.startYear !== nowFy.startYear) return;
    const { profit, gst } = computeJobFinancials(job);
    fyProfit += profit;
    fyTax += gst;
  });

  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    if (!equipmentForm.name) {
      toast.error('Please enter equipment name');
      return;
    }
    setEquipmentLoading(true);
    try {
      await createEquipment({
        ...equipmentForm,
        purchase_price: equipmentForm.purchase_price ? parseFloat(equipmentForm.purchase_price) : 0,
        daily_rate: equipmentForm.daily_rate ? parseFloat(equipmentForm.daily_rate) : 0,
        weekly_rate: equipmentForm.weekly_rate ? parseFloat(equipmentForm.weekly_rate) : null,
        purchase_date: equipmentForm.purchase_date || null,
      });
      toast.success('Equipment added successfully');
      setEquipmentDialogOpen(false);
      setEquipmentForm({ name: '', category: 'Camera', description: '', purchase_price: '', daily_rate: '', weekly_rate: '', purchase_date: '' });
      navigate('/equipment');
    } catch (error) {
      toast.error('Failed to add equipment');
    } finally {
      setEquipmentLoading(false);
    }
  };

  const addClientContact = () => {
    setClientForm({
      ...clientForm,
      contacts: [...clientForm.contacts, { name: '', email: '', phone: '' }],
    });
  };

  const updateClientContact = (index, field, value) => {
    const newContacts = [...clientForm.contacts];
    newContacts[index][field] = value;
    setClientForm({ ...clientForm, contacts: newContacts });
  };

  const removeClientContact = (index) => {
    const newContacts = clientForm.contacts.filter((_, i) => i !== index);
    setClientForm({ ...clientForm, contacts: newContacts });
  };

  // Open client edit dialog from search
  const handleClientEdit = (client) => {
    setEditingClient(client);
    setClientForm({
      business_name: client.business_name || '',
      billing_address: client.billing_address || '',
      contacts: client.contacts || [],
    });
    setClientDialogOpen(true);
  };

  const handleClientSubmit = async () => {
    if (!clientForm.business_name) {
      toast.error('Business name is required');
      return;
    }

    // Validate contacts - each contact must have an email
    for (let i = 0; i < clientForm.contacts.length; i++) {
      if (!clientForm.contacts[i].email || !clientForm.contacts[i].email.trim()) {
        toast.error(`Contact ${i + 1}: Email is required`);
        return;
      }
    }

    setClientLoading(true);
    try {
      if (editingClient) {
        await updateClient(editingClient.id, clientForm);
        toast.success('Client updated successfully');
      } else {
        await createClient(clientForm);
        toast.success('Client added successfully');
      }
      setClientDialogOpen(false);
      setEditingClient(null);
      setClientForm({ business_name: '', billing_address: '', contacts: [] });
    } catch (error) {
      toast.error(editingClient ? 'Failed to update client' : 'Failed to add client');
    } finally {
      setClientLoading(false);
    }
  };

  // Show loading state while jobs are being fetched
  if (jobsLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-12 animate-in" data-testid="dashboard-page">
        {/* System Alerts for Admins */}
        <SystemAlertsBanner />
        
        {/* First-time user - Getting Started view */}
        {showOnboarding ? (
          <>
            {/* Header */}
            <PageHeader title="Getting started" />

            {/* Three large onboarding panels */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Panel 1 - Add Equipment */}
              <Card 
                className={`relative overflow-hidden cursor-pointer ring-1 transition-colors ${hasEquipment ? 'ring-moss/40' : 'ring-transparent hover:ring-line-strong'}`}
                onClick={() => !hasEquipment && setEquipmentDialogOpen(true)}
              >
                <div className={`absolute top-4 left-4 w-10 h-10 rounded-pill flex items-center justify-center text-xl ${hasEquipment ? 'bg-moss-soft text-moss' : 'bg-inverse text-inverse-foreground'}`}>
                  {hasEquipment ? <Check className="w-[18px] h-[18px]" /> : '1'}
                </div>
                <Illustration name="equipment" className="w-full" />
                <CardContent className="pt-2 pb-8 flex flex-col items-center text-center">
                  <h3 className="section-title mb-2">{hasEquipment ? 'Equipment added!' : 'Add your equipment'}</h3>
                  <p className="text-muted-foreground mb-6">
                    {hasEquipment ? 'Great! You can add more anytime from the Equipment page.' : 'Track your gear, calculate ROI, and manage your kit inventory.'}
                  </p>
                  {!hasEquipment && (
                    <Button size="lg">
                      <Package className="w-4 h-4" /> Add equipment
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Panel 2 - Add Client */}
              <Card 
                className={`relative overflow-hidden cursor-pointer ring-1 transition-colors ${hasClients ? 'ring-moss/40' : 'ring-transparent hover:ring-line-strong'}`}
                onClick={() => !hasClients && setClientDialogOpen(true)}
              >
                <div className={`absolute top-4 left-4 w-10 h-10 rounded-pill flex items-center justify-center text-xl ${hasClients ? 'bg-moss-soft text-moss' : 'bg-inverse text-inverse-foreground'}`}>
                  {hasClients ? <Check className="w-[18px] h-[18px]" /> : '2'}
                </div>
                <Illustration name="clients" className="w-full" />
                <CardContent className="pt-2 pb-8 flex flex-col items-center text-center">
                  <h3 className="section-title mb-2">{hasClients ? 'Client added!' : 'Add your clients'}</h3>
                  <p className="text-muted-foreground mb-6">
                    {hasClients ? 'Perfect! You can add more from the Clients page.' : 'Store client details for quick estimates and job management.'}
                  </p>
                  {!hasClients && (
                    <Button size="lg">
                      <Users className="w-4 h-4" /> Add client
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Panel 3 - Create Job */}
              <Card 
                className="relative overflow-hidden cursor-pointer ring-1 ring-transparent hover:ring-line-strong transition-colors"
                onClick={() => navigate('/jobs/new')}
              >
                <div className="absolute top-4 left-4 w-10 h-10 rounded-pill bg-inverse text-inverse-foreground flex items-center justify-center text-xl">
                  3
                </div>
                <Illustration name="job" className="w-full" />
                <CardContent className="pt-2 pb-8 flex flex-col items-center text-center">
                  <h3 className="section-title mb-2">Create your first job</h3>
                  <p className="text-muted-foreground mb-6">
                    Log jobs, track time, and generate estimates for your clients.
                  </p>
                  <Button size="lg">
                    <Plus className="w-4 h-4" /> New job
                  </Button>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <>
            {/* Regular Dashboard for existing users */}
            {/* Header and toolbar: search left, actions right. Not wrapped:
                the toolbar sticks only while its parent is on screen */}
              <PageHeader title="Dashboard" />

              {isPhone ? (
                <PhonePanel
                  actions={[
                    { label: 'Add equipment', onClick: () => setEquipmentDialogOpen(true) },
                    { label: 'Add client', onClick: () => setClientDialogOpen(true) },
                    { label: 'New job', icon: Plus, to: '/jobs/new', primary: true, testId: 'new-job-btn' },
                  ]}
                />
              ) : (
              <PageToolbar>
                <div className="flex-1 lg:max-w-md">
                  <UniversalSearch onClientEdit={handleClientEdit} />
                </div>
                <div className="flex flex-wrap gap-3 shrink-0 lg:ml-auto">
                  <Button variant="outline" onClick={() => setEquipmentDialogOpen(true)}>
                    <Package className="w-4 h-4" /> Add equipment
                  </Button>
                  <Button variant="outline" onClick={() => setClientDialogOpen(true)}>
                    <Users className="w-4 h-4" /> Add client
                  </Button>
                  <Button asChild data-testid="new-job-btn">
                    <Link to="/jobs/new">
                      <Plus className="w-4 h-4" /> New job
                    </Link>
                  </Button>
                </div>
              </PageToolbar>
              )}

            {/* Row 1: Stats. Not on a phone, where the job columns come first */}
            <div className="hidden md:grid grid-cols-3 gap-4">
              {/* Current Jobs Overview - Clickable */}
              <Card 
                data-testid="stat-jobs" 
                className="relative overflow-hidden rounded-tile p-6 lg:pr-24 cursor-pointer transition-colors hover:bg-mist"
                onClick={() => navigate('/jobs')}
              >
                <StatArt name="statJobs" />
                <p className="eyebrow">current jobs</p>
                <div className="flex items-end gap-4 flex-wrap mt-3">
                  <div>
                    <div className="text-5xl lg:text-[80px] font-light leading-none tracking-tight">{bookedJobs}</div>
                    <p className="text-sm text-muted-foreground mt-1">Booked</p>
                  </div>
                  <div className="text-ink-faded pb-6" aria-hidden="true">|</div>
                  <div>
                    <div className="text-3xl font-light leading-none tracking-tight">{jobsToChase}</div>
                    <p className="text-sm text-muted-foreground mt-1">To chase</p>
                  </div>
                  <div className="text-ink-faded pb-6" aria-hidden="true">|</div>
                  <div>
                    <div className="text-3xl font-light leading-none tracking-tight text-muted-foreground">{bookedJobs + jobsToChase}</div>
                    <p className="text-sm text-muted-foreground mt-1">Total</p>
                  </div>
                </div>
              </Card>

              {/* Potential Billings - Open opportunities (draft + sent jobs) */}
              <Card data-testid="stat-potential" className="relative overflow-hidden rounded-tile p-6 lg:pr-24">
                <StatArt name="statBillings" />
                <p className="eyebrow">potential billings</p>
                <div className="mt-3 text-4xl xl:text-5xl font-light leading-none tracking-tight">
                  {formatCurrency(potentialTotal)}
                </div>
                <div className="flex flex-wrap gap-x-2 gap-y-1 mt-3 text-sm text-muted-foreground">
                  <span>Labour: {formatCurrency(potentialLabour)}</span>
                  <span className="text-ink-faded" aria-hidden="true">·</span>
                  <span>Equip: {formatCurrency(potentialEquipment)}</span>
                </div>
              </Card>

              {/* This Financial Year - Profit & tax collected (completed/paid jobs) */}
              <Card
                data-testid="stat-fy-profit"
                className="relative overflow-hidden rounded-tile p-6 lg:pr-24 cursor-pointer transition-opacity hover:opacity-95 bg-inverse text-inverse-foreground"
                onClick={() => navigate('/jobs')}
              >
                <StatArt name="statProfit" />
                <p className="eyebrow text-inverse-muted flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> {fyLabel} profit
                </p>
                <div className={`mt-3 text-4xl xl:text-5xl font-light leading-none tracking-tight ${fyProfit >= 0 ? 'text-inverse-foreground' : 'text-brick-soft'}`} data-testid="fy-profit-value">
                  {formatCurrency(fyProfit)}
                </div>
                <div className="flex gap-2 mt-3 text-sm text-inverse-muted">
                  <span data-testid="fy-tax-value">{getTaxName()} collected: {formatCurrency(fyTax)}</span>
                </div>
              </Card>
            </div>

            {/* Row 2+: All Active Jobs Grid */}
            <div data-testid="recent-jobs-card">
              {(() => {
                // Filter to active jobs only - exclude completed, paid, and lost jobs
                const activeJobs = jobs.filter(job => 
                  job.status !== 'completed' && 
                  job.status !== 'paid' && 
                  job.status !== 'lost'
                );
                
                if (activeJobs.length === 0) {
                  return (
                    <Card>
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Illustration name="job" className="w-full max-w-xs mb-2" />
                        <h3 className="section-title mb-2">No active jobs</h3>
                        <p className="text-muted-foreground text-center mb-4">
                          Your drafts, sent quotes and booked jobs will show here
                        </p>
                        <Button asChild>
                          <Link to="/jobs/new">
                            <Plus className="w-4 h-4" /> New job
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  );
                }
                
                return (
                  <JobKanban jobs={activeJobs} formatCurrency={formatCurrency} />
                );
              })()}
            </div>

          </>
        )}
      </div>

      {/* Equipment Dialog */}
      <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add equipment</DialogTitle>
            <DialogDescription>Add a new piece of equipment to track</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEquipmentSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="equip-name">Name *</Label>
              <Input
                id="equip-name"
                value={equipmentForm.name}
                onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })}
                placeholder="e.g., ARRI Alexa Mini"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="equip-category">Category</Label>
              <Select
                value={equipmentForm.category}
                onValueChange={(value) => setEquipmentForm({ ...equipmentForm, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="equip-description">Description</Label>
              <Textarea
                id="equip-description"
                value={equipmentForm.description}
                onChange={(e) => setEquipmentForm({ ...equipmentForm, description: e.target.value })}
                placeholder="Package contents, serial numbers, etc."
                className="h-20"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="equip-price">Purchase price ({regionConfig?.currency?.symbol || '$'})</Label>
                <Input
                  id="equip-price"
                  type="number"
                  step="0.01"
                  value={equipmentForm.purchase_price}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, purchase_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="equip-rate">Daily rate ({regionConfig?.currency?.symbol || '$'})</Label>
                <Input
                  id="equip-rate"
                  type="number"
                  step="0.01"
                  value={equipmentForm.daily_rate}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, daily_rate: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="equip-weekly-rate">Weekly rate ({regionConfig?.currency?.symbol || '$'}) <span className="text-muted-foreground font-normal text-xs">optional</span></Label>
                <Input
                  id="equip-weekly-rate"
                  type="number"
                  step="0.01"
                  value={equipmentForm.weekly_rate}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, weekly_rate: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEquipmentDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={equipmentLoading}>
                {equipmentLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Add equipment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit client' : 'Add new client'}</DialogTitle>
            <DialogDescription>{editingClient ? 'Update client information' : 'Add a new client to your database'}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client-name">Business name *</Label>
              <Input
                id="client-name"
                value={clientForm.business_name}
                onChange={(e) => setClientForm({ ...clientForm, business_name: e.target.value })}
                placeholder="Client business name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="client-address">Billing address</Label>
              <Input
                id="client-address"
                value={clientForm.billing_address}
                onChange={(e) => setClientForm({ ...clientForm, billing_address: e.target.value })}
                placeholder="123 Business St, Sydney NSW 2000"
              />
            </div>

            {/* Contacts Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Contacts</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addClientContact}
                >
                  <UserPlus className="w-4 h-4" />
                  Add contact
                </Button>
              </div>

              {clientForm.contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-line rounded-cards">
                  No contacts yet. Click &ldquo;Add contact&rdquo; to add one.
                </p>
              ) : (
                <div className="space-y-3">
                  {clientForm.contacts.map((contact, index) => (
                    <Card key={index} className="p-3 rounded-cards bg-mist">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm">Contact {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeClientContact(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Name (optional)</Label>
                          <Input
                            placeholder="Contact name"
                            value={contact.name || ''}
                            onChange={(e) => updateClientContact(index, 'name', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Email *</Label>
                          <Input
                            type="email"
                            placeholder="contact@example.com"
                            value={contact.email}
                            onChange={(e) => updateClientContact(index, 'email', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Phone (optional)</Label>
                          <Input
                            placeholder={getPhonePlaceholder(region)}
                            value={contact.phone || ''}
                            onChange={(e) => updateClientContact(index, 'phone', e.target.value)}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setClientDialogOpen(false);
              setEditingClient(null);
              setClientForm({ business_name: '', billing_address: '', contacts: [] });
            }}>Cancel</Button>
            <Button onClick={handleClientSubmit} disabled={clientLoading}>
              {clientLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingClient ? 'Save changes' : 'Create client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
